/**
 * Merged sector groups for home matches only.
 * When admin selects one sector in a group, all sectors in that group are selected for the same price.
 * When user selects one sector in a group, the whole group is considered selected (same price).
 *
 * Groups:
 * - 106 to 109
 * - 206 to 210
 * - 118 to 122
 * - 222 to 228
 */
export const HOME_MERGED_SECTOR_GROUPS = [
  [106, 107, 108, 109],
  [206, 207, 208, 209, 210],
  [118, 119, 120, 121, 122],
  [222, 223, 224, 225, 226, 227, 228],
];

/**
 * Get the merged group (array of sector numbers) for a sector, or null if not in a merged group.
 * @param {number|string} sectorNum - Sector number (e.g. 106 or "106")
 * @returns {number[]|null}
 */
export function getMergedGroupForSector(sectorNum) {
  const num = typeof sectorNum === 'string' ? parseInt(sectorNum, 10) : sectorNum;
  if (isNaN(num)) return null;
  const group = HOME_MERGED_SECTOR_GROUPS.find((g) => g.includes(num));
  return group ? [...group] : null;
}

/**
 * Get sector strings for the merged group (for admin tempSelectedSectors / pricing).
 * @param {number|string} sectorNum
 * @returns {string[]|null} e.g. ["106","107","108","109"] or null
 */
export function getMergedSectorStringsForHome(sectorNum) {
  const group = getMergedGroupForSector(sectorNum);
  return group ? group.map((n) => String(n)) : null;
}

/**
 * Check if any sector in the merged group is in the given list (e.g. already in a pricing group).
 * @param {number|string} sectorNum
 * @param {string[]} sectorList - e.g. group.sectors or flat list of sector names
 * @returns {boolean}
 */
export function isMergedGroupInList(sectorNum, sectorList) {
  const group = getMergedSectorStringsForHome(sectorNum);
  if (!group) return sectorList.includes(String(sectorNum));
  return group.some((s) => sectorList.includes(s));
}

/**
 * Map a stadium sector number to its zone label (home matches).
 * @param {number|string} sectorNum
 * @returns {string}
 */
export function getZoneFromSector(sectorNum) {
  if (typeof sectorNum === 'string' && sectorNum.toLowerCase().includes('legends')) {
    return 'Legends Club';
  }

  const sector = parseInt(sectorNum, 10);
  if (Number.isNaN(sector)) return '';

  if ([106, 107, 108, 109].includes(sector)) {
    return 'Curva Nord1';
  }

  if ([206, 207, 208, 209, 210].includes(sector)) {
    return 'Curva Nord2';
  }

  if ([118, 119, 120, 121, 122].includes(sector)) {
    return 'Curva Sud1';
  }

  if ([222, 223, 224, 225, 226, 227, 228].includes(sector)) {
    return 'Curva Sud2';
  }

  if ([111, 112, 113, 114, 115, 116, 117, 213, 214, 215, 216, 217, 218, 219, 220, 221].includes(sector)) {
    return 'Tribuna Est';
  }

  if (
    [101, 102, 103, 104, 105, 123, 124, 125, 126, 201, 202, 203, 204, 205, 229, 230, 231, 232].includes(
      sector,
    )
  ) {
    return 'Tribuna Ovest';
  }

  if ([110, 211, 212].includes(sector)) {
    return 'Secteur Ospiti';
  }

  return '';
}
